import React, { useEffect, useMemo, useState } from 'react';
import { http } from "../lib/axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AdminWorkspaceNav from '../components/AdminWorkspaceNav';
import ContextHelpButton from '../components/ContextHelpButton';

const ART_PREFERENCE_OPTIONS = [
  'Painting',
  'Sculpture',
  'Photography',
  'Mixed Media',
  'Digital',
  'Installation',
  'Drawing',
  'Print',
];

const glassPanelSx = {
  backgroundColor: 'rgba(255,255,255,0.72)',
  backdropFilter: 'blur(14px)',
  borderRadius: 3,
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
};

const sectionWidthSx = {
  width: '100%',
  maxWidth: 1120,
  mx: 'auto',
};

function buyerDisplayName(buyer) {
  const first = buyer?.user?.first_name || '';
  const last = buyer?.user?.last_name || '';
  return `${first} ${last}`.trim() || buyer?.user?.username || 'Unnamed Buyer';
}

function profileCompleteness(buyer) {
  const checks = [
    buyer?.user?.first_name,
    buyer?.user?.last_name,
    buyer?.address,
    Array.isArray(buyer?.art_preferences) && buyer.art_preferences.length > 0,
    buyer?.location,
  ];
  const complete = checks.filter(Boolean).length;
  return Math.round((complete / checks.length) * 100);
}

function missingBuyerFields(buyer) {
  const missing = [];
  if (!buyer?.user?.first_name) missing.push('First name');
  if (!buyer?.user?.last_name) missing.push('Last name');
  if (!buyer?.address) missing.push('Address');
  if (!Array.isArray(buyer?.art_preferences) || buyer.art_preferences.length === 0) {
    missing.push('Art preferences');
  }
  return missing;
}

function favoriteSummaryChips(buyer) {
  return [
    `${buyer?.favorite_artworks_count || 0} favorite artworks`,
    `${buyer?.favorite_artists_count || 0} favorite artists`,
    `${buyer?.favorite_hosts_count || 0} favorite hosts`,
  ];
}

export default function BuyerManager() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [preferenceFilter, setPreferenceFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [editingBuyer, setEditingBuyer] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadBuyers = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const response = await http.get('/buyers/');
      setBuyers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Unable to load buyer profiles.');
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadBuyers();
  }, []);

  const filteredBuyers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return buyers.filter((buyer) => {
      const haystack = [
        buyerDisplayName(buyer),
        buyer?.user?.username,
        buyer?.user?.email,
        buyer?.address,
        ...(buyer?.art_preferences || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = !q || haystack.includes(q);
      const preferenceMatch =
        preferenceFilter === 'all' ||
        (Array.isArray(buyer?.art_preferences) && buyer.art_preferences.includes(preferenceFilter));
      const completeness = profileCompleteness(buyer);
      const completenessMatch =
        completenessFilter === 'all' ||
        (completenessFilter === 'attention' && completeness < 70) ||
        (completenessFilter === 'strong' && completeness >= 70);

      return searchMatch && preferenceMatch && completenessMatch;
    });
  }, [buyers, search, preferenceFilter, completenessFilter]);

  const summary = useMemo(() => {
    const withPreferences = buyers.filter((buyer) => Array.isArray(buyer?.art_preferences) && buyer.art_preferences.length > 0).length;
    const needsAttention = buyers.filter((buyer) => profileCompleteness(buyer) < 70).length;
    const withPurchases = buyers.filter((buyer) => (buyer?.purchase_count || 0) > 0).length;
    const refundRequested = buyers.reduce((sum, buyer) => sum + (buyer?.refund_status_summary?.refund_requested || 0), 0);
    const avgCompleteness = buyers.length
      ? Math.round(buyers.reduce((sum, buyer) => sum + profileCompleteness(buyer), 0) / buyers.length)
      : 0;

    return {
      total: buyers.length,
      withPreferences,
      withPurchases,
      refundRequested,
      needsAttention,
      avgCompleteness,
    };
  }, [buyers]);

  const openEditDialog = (buyer) => {
    setEditingBuyer(buyer);
    setEditForm({
      user: {
        first_name: buyer?.user?.first_name || '',
        last_name: buyer?.user?.last_name || '',
      },
      address: buyer?.address || '',
      art_preferences: Array.isArray(buyer?.art_preferences) ? buyer.art_preferences : [],
    });
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditingBuyer(null);
    setEditForm(null);
  };

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUserField = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!editingBuyer?.id || !editForm) return;
    setSaving(true);
    try {
      await http.patch(`/buyers/${editingBuyer.id}/`, editForm);
      await loadBuyers({ silent: true });
      setSnackbar({ open: true, message: 'Buyer profile updated.', severity: 'success' });
      closeEditDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to update the buyer profile.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await http.delete(`/buyers/${deleteTarget.id}/`);
      setBuyers((prev) => prev.filter((buyer) => buyer.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'Buyer profile deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to delete the buyer profile.',
        severity: 'error',
      });
    }
  };

  return (
    <Container sx={{ mt: 5, mb: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              Buyer Manager
            </Typography>
            <ContextHelpButton section="admin-help" title="Help for buyer manager" />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage existing buyer profiles, review profile readiness, and update the buyer-facing fields that drive discovery preferences and purchase-related workflows.
          </Typography>
        </Box>

        <Box sx={sectionWidthSx}>
          <AdminWorkspaceNav subtitle="Manage buyer profiles inside the shared admin workspace so all admin pages follow the same navigation model." />
        </Box>

        <Grid container spacing={2} sx={sectionWidthSx}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Buyers</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">With Preferences</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withPreferences}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Needs Attention</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.needsAttention}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Average Profile Completeness</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.avgCompleteness}%</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Buyers With Purchases</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withPurchases}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Refund Requests</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.refundRequested}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search buyers"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, username, email, address, preferences..."
              fullWidth
            />
            <TextField
              select
              label="Art preference"
              value={preferenceFilter}
              onChange={(event) => setPreferenceFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 220 } }}
            >
              <MenuItem value="all">All preferences</MenuItem>
              {ART_PREFERENCE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Completeness"
              value={completenessFilter}
              onChange={(event) => setCompletenessFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              <MenuItem value="all">All profiles</MenuItem>
              <MenuItem value="attention">Needs attention</MenuItem>
              <MenuItem value="strong">70%+ complete</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setPreferenceFilter('all');
                setCompletenessFilter('all');
              }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" onClick={() => loadBuyers({ silent: true })} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredBuyers.length} of {buyers.length} buyer profiles.
          </Typography>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography>Loading buyer profiles…</Typography>
          </Paper>
        ) : filteredBuyers.length === 0 ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography color="text.secondary">No buyer profiles match the current filters.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={sectionWidthSx}>
            {filteredBuyers.map((buyer) => (
              <Grid item xs={12} key={buyer.id}>
                <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', lg: 'stretch' }}
                    sx={{ minHeight: { lg: 180 } }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {buyerDisplayName(buyer)}
                        </Typography>
                        <Chip size="small" label={`Completeness ${profileCompleteness(buyer)}%`} />
                        {profileCompleteness(buyer) < 70 && (
                          <Chip size="small" color="warning" label="Needs attention" />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        @{buyer?.user?.username} | {buyer?.user?.email || 'No email'}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {buyer?.address || 'No address on file yet.'}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                        <Chip size="small" label={`${buyer?.purchase_count || 0} purchases`} />
                        <Chip size="small" label={`${buyer?.refund_status_summary?.refund_requested || 0} refund requested`} />
                        <Chip size="small" label={`${buyer?.refund_status_summary?.refunded || 0} refunded`} />
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {(buyer?.art_preferences || []).length > 0 ? (
                          buyer.art_preferences.map((preference) => (
                            <Chip key={`${buyer.id}-${preference}`} size="small" variant="outlined" label={preference} />
                          ))
                        ) : (
                          <Chip size="small" variant="outlined" label="No art preferences" />
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {favoriteSummaryChips(buyer).map((label) => (
                          <Chip key={`${buyer.id}-${label}`} size="small" label={label} />
                        ))}
                      </Stack>

                      <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        <Button variant="outlined" onClick={() => setSelectedBuyer(buyer)}>
                          View Details
                        </Button>
                        <Button variant="outlined" onClick={() => openEditDialog(buyer)}>
                          Edit
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => setDeleteTarget(buyer)}>
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog open={Boolean(selectedBuyer)} onClose={() => setSelectedBuyer(null)} fullWidth maxWidth="md">
        <DialogTitle>Buyer Profile Details</DialogTitle>
        <DialogContent dividers>
          {selectedBuyer && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {buyerDisplayName(selectedBuyer)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{selectedBuyer?.user?.username} | {selectedBuyer?.user?.email || 'No email'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Completeness ${profileCompleteness(selectedBuyer)}%`} />
                <Chip
                  color={(selectedBuyer?.art_preferences || []).length > 0 ? 'success' : 'default'}
                  label={(selectedBuyer?.art_preferences || []).length > 0 ? 'Preferences set' : 'Preferences missing'}
                />
                <Chip label={`${selectedBuyer?.purchase_count || 0} purchases`} />
                <Chip label={`${selectedBuyer?.refund_status_summary?.refund_requested || 0} refund requested`} />
                <Chip label={`${selectedBuyer?.refund_status_summary?.refunded || 0} refunded`} />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{selectedBuyer?.address || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{selectedBuyer?.user?.email || 'Not provided'}</Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Art Preferences</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(selectedBuyer?.art_preferences || []).length > 0 ? (
                    selectedBuyer.art_preferences.map((preference) => (
                      <Chip key={`${selectedBuyer.id}-${preference}`} size="small" variant="outlined" label={preference} />
                    ))
                  ) : (
                    <Chip size="small" variant="outlined" label="No art preferences" />
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Favorites Summary</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {favoriteSummaryChips(selectedBuyer).map((label) => (
                    <Chip key={`${selectedBuyer.id}-${label}`} size="small" label={label} />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Recent Purchases</Typography>
                <Stack spacing={1}>
                  {(selectedBuyer?.recent_purchases || []).length > 0 ? (
                    selectedBuyer.recent_purchases.map((purchase) => (
                      <Paper key={purchase.payment_id} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {purchase.artwork_title || 'Untitled artwork'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status: {purchase.status} | Amount: ${purchase.amount} | Date: {purchase.sale_date || 'Unknown'}
                        </Typography>
                      </Paper>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No purchases found for this buyer yet.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Refund Status Summary</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`${selectedBuyer?.refund_status_summary?.refund_requested || 0} refund requested`} />
                  <Chip label={`${selectedBuyer?.refund_status_summary?.refund_pending || 0} refund pending`} />
                  <Chip label={`${selectedBuyer?.refund_status_summary?.refund_rejected || 0} refund rejected`} />
                  <Chip label={`${selectedBuyer?.refund_status_summary?.refunded || 0} refunded`} />
                </Stack>
              </Box>

              <Alert severity={missingBuyerFields(selectedBuyer).length > 0 ? 'warning' : 'success'}>
                {missingBuyerFields(selectedBuyer).length > 0
                  ? `Missing profile details: ${missingBuyerFields(selectedBuyer).join(', ')}`
                  : 'This buyer profile has the core fields filled in.'}
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedBuyer(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = selectedBuyer;
              setSelectedBuyer(null);
              if (target) openEditDialog(target);
            }}
          >
            Edit Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingBuyer)} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Edit Buyer Profile</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  value={editForm.user.first_name}
                  onChange={(event) => handleUserField('first_name', event.target.value)}
                  fullWidth
                />
                <TextField
                  label="Last name"
                  value={editForm.user.last_name}
                  onChange={(event) => handleUserField('last_name', event.target.value)}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Address"
                value={editForm.address}
                onChange={(event) => handleEditField('address', event.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Art preferences"
                SelectProps={{
                  multiple: true,
                  value: editForm.art_preferences,
                  onChange: (event) => handleEditField('art_preferences', event.target.value),
                  renderValue: (selected) => selected.join(', '),
                }}
                fullWidth
              >
                {ART_PREFERENCE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Buyer Profile</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete the buyer profile for <strong>{deleteTarget ? buyerDisplayName(deleteTarget) : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This is an admin action and may remove live buyer data, so it should be used carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
